import type { ApiBody, ApiQuery } from '../../../shared/api/contracts'
import { apiRequest, apiGet, apiPost, apiPut, apiDelete } from './transport'
import { financialQuery } from './financialQuery'

export async function fetchGoals(filters?: ApiQuery<'GET /admin/goals'>, signal?: AbortSignal) {
  const u = new URLSearchParams()
  if (filters?.status) u.set('status', filters.status)
  if (filters?.type) u.set('type', filters.type)
  if (filters?.includeArchived) u.set('includeArchived', 'true')
  if (filters?.search) u.set('search', filters.search)
  if (filters?.month) u.set('month', filters.month)
  const qs = u.toString()
  return apiGet<'GET /admin/goals'>(`/admin/goals${qs ? `?${qs}` as const : ''}`, signal)
}

export async function fetchGoal(id: string, signal?: AbortSignal) {
  return apiGet<'GET /admin/goals/:id'>(`/admin/goals/${encodeURIComponent(id)}`, signal)
}

export async function createGoal(payload: ApiBody<'POST /admin/goals'>, signal?: AbortSignal) {
  return apiPost<'POST /admin/goals'>('/admin/goals', payload, signal)
}

export async function updateGoal(id: string, patch: ApiBody<'PUT /admin/goals/:id'>, signal?: AbortSignal) {
  return apiPut<'PUT /admin/goals/:id'>(`/admin/goals/${encodeURIComponent(id)}`, patch, signal)
}

export async function pauseGoal(id: string, signal?: AbortSignal) {
  return apiPost<'POST /admin/goals/:id/pause'>(`/admin/goals/${encodeURIComponent(id)}/pause`, undefined, signal)
}

export async function archiveGoal(id: string, signal?: AbortSignal) {
  return apiPost<'POST /admin/goals/:id/archive'>(`/admin/goals/${encodeURIComponent(id)}/archive`, undefined, signal)
}

export async function completeGoal(id: string, signal?: AbortSignal) {
  return apiPost<'POST /admin/goals/:id/complete'>(`/admin/goals/${encodeURIComponent(id)}/complete`, undefined, signal)
}

export async function deleteGoal(id: string, signal?: AbortSignal) {
  return apiDelete<'DELETE /admin/goals/:id'>(`/admin/goals/${encodeURIComponent(id)}`, signal)
}

export async function fetchGoalContributions(goalId: string, signal?: AbortSignal) {
  return apiGet<'GET /admin/goals/:id/contributions'>(`/admin/goals/${encodeURIComponent(goalId)}/contributions`, signal)
}

export async function createGoalContribution(goalId: string, payload: ApiBody<'POST /admin/goals/:id/contributions'>, signal?: AbortSignal) {
  return apiPost<'POST /admin/goals/:id/contributions'>(`/admin/goals/${encodeURIComponent(goalId)}/contributions`, payload, signal)
}

export async function updateGoalContribution(goalId: string, contributionId: string, patch: ApiBody<'PUT /admin/goals/:id/contributions/:contributionId'>, signal?: AbortSignal) {
  return apiPut<'PUT /admin/goals/:id/contributions/:contributionId'>(
    `/admin/goals/${encodeURIComponent(goalId)}/contributions/${encodeURIComponent(contributionId)}`,
    patch, signal
  )
}

export async function markGoalContributionPaid(
  goalId: string,
  contributionId: string,
  payload: ApiBody<'POST /admin/goals/:id/contributions/:contributionId/pay'>, signal?: AbortSignal
) {
  return apiPost<'POST /admin/goals/:id/contributions/:contributionId/pay'>(
    `/admin/goals/${encodeURIComponent(goalId)}/contributions/${encodeURIComponent(contributionId)}/pay`,
    payload, signal
  )
}

export async function fetchUpcomingGoalContributions(params?: ApiQuery<'GET /admin/goals/upcoming'>, signal?: AbortSignal) {
  const u = new URLSearchParams()
  if (params?.from) u.set('from', params.from)
  if (params?.to) u.set('to', params.to)
  if (params?.limit != null) u.set('limit', String(params.limit))
  const qs = u.toString()
  return apiGet<'GET /admin/goals/upcoming'>(`/admin/goals/upcoming${qs ? `?${qs}` as const : ''}`, signal)
}

export async function fetchGoalsSummary(params?: ApiQuery<'GET /admin/goals/summary'>, signal?: AbortSignal) {
  const u = new URLSearchParams()
  if (params?.month) u.set('month', params.month)
  const qs = u.toString()
  return apiGet<'GET /admin/goals/summary'>(`/admin/goals/summary${qs ? `?${qs}` as const : ''}`, signal)
}

export async function fetchGoalReminderSettings(goalId: string, signal?: AbortSignal) {
  return apiGet<'GET /admin/goals/:id/reminders'>(
    `/admin/goals/${encodeURIComponent(goalId)}/reminders`, signal
  )
}

export async function updateGoalReminderSettings(goalId: string, payload: ApiBody<'PUT /admin/goals/:id/reminders'>, signal?: AbortSignal) {
  return apiPut<'PUT /admin/goals/:id/reminders'>(`/admin/goals/${encodeURIComponent(goalId)}/reminders`, payload, signal)
}

export function fetchGoalsOverview(params: ApiQuery<'GET /admin/goals/overview'>, signal?: AbortSignal) {
  return apiRequest<'GET /admin/goals/overview'>(`/admin/goals/overview?${financialQuery(params)}`, { signal })
}

export function fetchGoalPage(params: ApiQuery<'GET /admin/goals/page'>, signal?: AbortSignal) {
  return apiRequest<'GET /admin/goals/page'>(`/admin/goals/page?${financialQuery(params)}`, { signal })
}

export function fetchGoalDetails(id: string, params: ApiQuery<'GET /admin/goals/:id/overview'>, signal?: AbortSignal) {
  return apiRequest<'GET /admin/goals/:id/overview'>(`/admin/goals/${encodeURIComponent(id)}/overview?${financialQuery(params)}`, { signal })
}
